import { NO_BADGE } from '@/helpers/deals/resolveDealBadge'
import type {
  DealBadgeType,
  ResolvedDealBadge,
} from '@/helpers/deals/resolveDealBadge'

export type DealBadgeSegmentProps = {
  // Display group: always present.
  deal_badge_value: string
  is_deal_badge_shown: boolean
  deal_badge_type: DealBadgeType
  // Eligibility group: present exactly when eligibility was computed.
  // Otherwise the keys are absent, not present as `undefined`.
  is_low_bidder_deal_eligible?: boolean
  deal_badge_category?: string | null
}

/**
 * GLOB-5164 — the deal-badge Segment properties for one resolved listing.
 * Emitters spread this rather than naming the properties, so no event can
 * drift from the others. The bidder-count pair is `toBidderCountSegmentProps`'s.
 *
 * `rendered: false` is for List view, which shows no badge: the display group
 * reports none, while eligibility, which depends on the listing and the time
 * but not on the viewer, is kept as computed.
 */
export const toDealBadgeSegmentProps = (
  {
    deal_badge_value,
    is_deal_badge_shown,
    dealBadgeType,
    lowBidder,
  }: ResolvedDealBadge,
  { rendered = true }: { rendered?: boolean } = {}
): DealBadgeSegmentProps => ({
  deal_badge_value: rendered ? deal_badge_value : NO_BADGE.deal_badge_value,
  is_deal_badge_shown: rendered
    ? is_deal_badge_shown
    : NO_BADGE.is_deal_badge_shown,
  deal_badge_type: rendered ? dealBadgeType : 'none',
  ...(lowBidder && {
    is_low_bidder_deal_eligible: lowBidder.eligible,
    deal_badge_category: lowBidder.matchedCategory,
  }),
})

type DealBadgeImpressionSummary = {
  is_deal_badge_shown: boolean
  // Absent, not `0`, when no card carries eligibility, mirroring the
  // per-card eligibility group.
  low_bidder_eligible_card_count?: number
}

/**
 * Payload-level deal-badge properties for a multi-card impression.
 * `is_deal_badge_shown` is true when any card reports a badge shown.
 */
export const summarizeDealBadgeImpressions = (
  cards: DealBadgeSegmentProps[]
): DealBadgeImpressionSummary => ({
  is_deal_badge_shown: cards.some((card) => card.is_deal_badge_shown),
  ...(cards.some((card) => 'is_low_bidder_deal_eligible' in card) && {
    low_bidder_eligible_card_count: cards.filter(
      (card) => card.is_low_bidder_deal_eligible === true
    ).length,
  }),
})
